import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import FindPropertyDialog from '../../application-core/associated-project/FindPropertyDialog';
import CommonIriDisplay from '../../application-core/CommonIriDisplay';
import Errored from '../../core/Errored';
import LabelledSwitch from '../../core/LabelledSwitch';
import Loading from '../../core/Loading';
import Notification from '../../core/Notification';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, getProjectFromGraph, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';

const loadOptions = {};

const numericDatatypes = [
  "http://www.w3.org/2001/XMLSchema#integer",
  "http://www.w3.org/2001/XMLSchema#decimal",
  "http://www.w3.org/2001/XMLSchema#double",
  "http://www.w3.org/2001/XMLSchema#float",
  "http://www.w3.org/2001/XMLSchema#long",
];

class PropertyDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      propertyData: {},
      foundGraphs: [],
      numerically: false,
      propertyGraph: props.graph,
      isErrored: false
    };
    this.cancellable = null;
  }

  componentDidMount() {
    if (this.props.resourceIRI && this.props.resourceIRI !== undefined) {
      this.collectResourceData();
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  loadData(iriData, options) {
    this.cancellable = makeCancelable(GS.loadDataFromGraph(iriData, options, false));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  async collectResourceData() {
    try {
      GS.handle("collect-properties")
      if (this.cancellable && this.cancellable.isCancelled()) return;
      // First we load association resources
      const opaLoadData = this.props.ontologyProjectAssociation ?
        this.props.ontologyProjectAssociation.map(opa => {
          return {
            graph: this.props.graph,
            iri: opa
          };
        })
        :
        [];
      if (opaLoadData.length === 0) {
        this.setState({ isLoading: false, propertyGraph: this.props.graph, propertyData: {}, foundGraphs: [] });
        return;
      }
      await this.loadData(opaLoadData, {}).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      // We get all the association information
      const opaSet = this.props.ontologyProjectAssociation ?
        this.props.ontologyProjectAssociation.map(opa => GS.data(opa, this.props.graph))
        :
        [];
      // There is an edge case where the same IRI is in multiple ontologies - but totally unlikely
      const opaProjects = opaSet.map(opa => opa.associatedProject).sort();
      let graphIRI;
      let foundGraphs = [];
      for (let iri of opaProjects) {
        const ontologyGraphIRI = getGraphFromProject(iri);
        const loadInfo = {
          graph: ontologyGraphIRI,
          iri: this.props.resourceIRI
        };
        await this.loadData([loadInfo], loadOptions).promise;
        if (GS.isLoaded(this.props.resourceIRI, ontologyGraphIRI)) {
          graphIRI = ontologyGraphIRI;
          foundGraphs.push(graphIRI);
        }
      }
      if (this.cancellable && this.cancellable.isCancelled()) return;
      // Use load info to get the right graph. Just get first graph if multiple
      let propertyData = {};
      if (graphIRI) {
        propertyData = GS.data(this.props.resourceIRI, graphIRI, false);
      }
      else { graphIRI = this.props.graph }
      this.setState({ isLoading: false, propertyGraph: graphIRI, propertyData, foundGraphs });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  }

  render() {

    const { isReadOnly, defaultLang, title, resourceIRI, source, ontologyProjects, graph,
      onAddProperty, sortNumerically, handleSortChange } = this.props;
    const { propertyGraph, isErrored, isLoading, propertyData, foundGraphs } = this.state;

    if (isErrored) return <Errored dataTest="cp-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    const isNumericRange = propertyData && propertyData.range && propertyData.range.some(r => numericDatatypes.includes(r));
    const showAddProject = ontologyProjects && ontologyProjects.length > 0;

    return (
      <TitledPanel dataTest="sort-property" height="full" title={title} actions={showAddProject && !resourceIRI && !isReadOnly ?
        <FindPropertyDialog
          defaultLang={defaultLang}
          projects={ontologyProjects}
          allowObjectProperties={false}
          exclude={resourceIRI && resourceIRI !== undefined ? [resourceIRI] : []}
          onAddProperty={onAddProperty} />
        :
        null
      }>
        {resourceIRI &&
          <div>
            <CommonIriDisplay
              id={resourceIRI}
              compact={true}
              isReadOnly={isReadOnly}
              source={source}
              loadComponents={true}
              property="sortProperty"
              projectIRI={getProjectFromGraph(propertyGraph)}
              handleNotAvailable={true}
              graph={propertyGraph}
              sourceGraph={graph}
              viewLang={defaultLang}
              defaultLang={defaultLang} />
            {foundGraphs && foundGraphs.length > 1 &&
              <Notification dataTest="multiple-warning" text="This property exists in multiple associated projects. We select the first available for display purposes. This will not affect sorting, which is based on the IRI alone." />
            }
            {(isNumericRange || sortNumerically) &&
              <LabelledSwitch
                dataTest="treat-as-numeric"
                disabled={isReadOnly}
                checked={sortNumerically}
                handleChange={handleSortChange}
                label="Treat as Numeric" />
            }
            {!isNumericRange && sortNumerically &&
              <Notification text="Property is not valid for numeric sorting" error={false} />
            }
          </div>
        }
      </TitledPanel>
    );
  }
}

PropertyDisplay.propTypes = {
  isReadOnly: PropTypes.bool,
  title: PropTypes.string,
  resourceIRI: PropTypes.string,
  source: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  graph: PropTypes.string.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  property: PropTypes.string,
  sortNumerically: PropTypes.bool,
  ontologyProjects: PropTypes.array,
  onAddProperty: PropTypes.func,
  handleSortChange: PropTypes.func,
  values: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
};

export default withStyles(styles)(PropertyDisplay);